// IND-032 / IND-033: desperdicio mensual. Cálculos puros de la vista.
import type {
  WasteControlMonth,
  WasteControlView,
  WasteCostExclusionReason,
  WasteCostGroup,
  WasteCostMaterialGap,
  WasteCostView,
  WasteEpdmLinesMonth,
  WasteEpdmLinesView,
} from '../services/industrialCostService';
import { esNumberFormat } from './numberFormat';

export const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
export const MONTH_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Por encima del 25 % de su media, aviso; por debajo del 20 %, en azul.
const HIGH_FACTOR = 1.25;
const LOW_FACTOR = 0.8;

// «3.307» es 3.307 kg (como se ve en pantalla), no 3,307.
export function parseKg(value: string): number | null {
  const text = value.trim();
  if (text === '') return null;
  const plain = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(text) ? text.replace(/\./g, '') : text;
  return Number(plain.replace(',', '.'));
}

// Para precargar un campo: con coma, o parseKg leería 123.456 como miles.
export function kgDraft(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value).replace('.', ',');
}

export function groupPct(month: WasteControlMonth | undefined, group: string): number | null {
  return month?.groups.find((row) => row.group === group)?.pct ?? null;
}

export function wasteTone(value: number | null, average: number | null): 'high' | 'low' | null {
  if (value === null || average === null || average <= 0) return null;
  if (value > average * HIGH_FACTOR) return 'high';
  if (value < average * LOW_FACTOR) return 'low';
  return null;
}

// IND-038: EPDM por línea.
export const EPDM_LINE_TOLERANCE_KG = 1;
const kgFormat = esNumberFormat({ maximumFractionDigits: 0 });

export type EpdmLineMetric = 'kg' | 'pct' | 'per100';

/** Cifra de una línea en un mes. Un mes que no cuadra no da kg por 100 m. */
export function epdmLineCell(month: WasteEpdmLinesMonth | undefined, key: string, metric: EpdmLineMetric): number | null {
  const value = month?.values[key] ?? null;
  if (!month || value === null) return null;
  if (metric === 'kg') return value;
  if (metric === 'pct') return month.total_kg ? (value / month.total_kg) * 100 : null;
  return month.status === 'ok' ? per100(value, month.metres[key] ?? 0) : null;
}

/** Media simple, solo de los meses que cuadran. */
export function epdmLineAverage(view: WasteEpdmLinesView, key: string, metric: EpdmLineMetric): number | null {
  // IND-044: kg por 100 m ponderada: total kg ÷ total metros de los meses que cuadran.
  if (metric === 'per100') {
    const months = view.months.filter((month) => month.status === 'ok' && epdmLineCell(month, key, 'per100') !== null);
    const metres = months.reduce((total, month) => total + month.metres[key], 0);
    return metres > 0
      ? (months.reduce((total, month) => total + (month.values[key] ?? 0), 0) / metres) * 100
      : null;
  }
  const values = view.months
    .filter((month) => month.status === 'ok')
    .map((month) => epdmLineCell(month, key, metric))
    .filter((value): value is number => value !== null);
  return values.length ? values.reduce((total, value) => total + value, 0) / values.length : null;
}

function differenceText(difference: number): string {
  return `${difference < 0 ? 'faltan' : 'sobran'} ${kgFormat.format(Math.abs(difference))} kg`;
}

function joinNames(names: string[]): string {
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}` : names.join('');
}

// Lista común de la página: otros avisos se añaden aquí con el mismo formato.
export type WasteNotice = { key: string; title: string; detail: string; month: number | null };

export function wasteNotices(view: WasteEpdmLinesView): WasteNotice[] {
  return view.warnings.map((warning) => {
    if (warning.kind === 'unknown_line') {
      return {
        key: `line-${warning.name}`,
        title: `Línea no reconocida: ${warning.name}`,
        detail: 'Añádela como alias en la configuración de líneas y vuelve a importar el libro.',
        month: null,
      };
    }
    const name = MONTH_NAMES[warning.month - 1];
    const total = warning.total_kg === null ? 'total EPDM sin dato' : `total EPDM ${kgFormat.format(warning.total_kg)} kg`;
    const difference = warning.difference_kg === null ? '' : ` · ${differenceText(warning.difference_kg)}`;
    return {
      key: `month-${warning.month}`,
      title: warning.missing.length ? `${name}: ${joinNames(warning.missing)} sin dato` : `${name}: las líneas no cuadran`,
      detail: `Líneas ${kgFormat.format(warning.lines_kg)} kg · ${total}${difference}`,
      month: warning.month,
    };
  });
}

/** Comprobación en vivo de «Registrar desperdicio». */
export function epdmLinesCheck(values: Array<number | null>, total: number | null): { text: string; ok: boolean } | null {
  const known = values.filter((value): value is number => value !== null && Number.isFinite(value));
  if (!known.length) return null;
  const sum = known.reduce((result, value) => result + value, 0);
  const warning = 'Puedes guardar igual; saldrá como aviso.';
  if (total === null || !Number.isFinite(total)) {
    return { text: `Suma de líneas: ${kgFormat.format(sum)} kg · falta el total de EPDM. ${warning}`, ok: false };
  }
  const prefix = `Suma de líneas: ${kgFormat.format(sum)} de ${kgFormat.format(total)} kg`;
  if (Math.abs(sum - total) < EPDM_LINE_TOLERANCE_KG) return { text: `${prefix} · cuadra.`, ok: true };
  return { text: `${prefix} · ${differenceText(sum - total)}. ${warning}`, ok: false };
}

export type WasteMiniBar = {
  month: number;
  initial: string;
  name: string;
  pct: number;
  // Altura en % de la gráfica: escala propia del grupo, de 0 a su máximo.
  height: number;
  closed: boolean;
};

/** Mini gráfica de una tarjeta: los meses con dato, de enero al último mes
 *  cerrado, a escala de 0 al % máximo del año en ese grupo. */
export function wasteMiniBars(
  view: Pick<WasteControlView, 'months' | 'closed_month'>,
  group: string,
  average: number | null,
): { bars: WasteMiniBar[]; averageHeight: number | null } {
  const closed = view.closed_month;
  const months = view.months
    .filter((item) => closed !== null && item.month <= closed)
    .map((item) => ({ month: item.month, pct: groupPct(item, group) }))
    .filter((item): item is { month: number; pct: number } => item.pct !== null);
  const top = Math.max(...months.map((item) => item.pct), 0);
  const height = (value: number) => (top > 0 ? Math.min(100, Math.max(0, (value / top) * 100)) : 0);
  return {
    bars: months.map((item) => ({
      month: item.month,
      initial: MONTH_NAMES[item.month - 1][0],
      name: MONTH_NAMES[item.month - 1].toLowerCase(),
      pct: item.pct,
      height: height(item.pct),
      closed: item.month === closed,
    })),
    averageHeight: average === null || top <= 0 ? null : height(average),
  };
}

function sumKnown(values: Array<number | null>): number | null {
  const known = values.filter((value): value is number => value !== null);
  return known.length ? known.reduce((total, value) => total + value, 0) : null;
}

export function per100(value: number | null, metres: number): number | null {
  return value === null || metres <= 0 ? null : (value / metres) * 100;
}

export type WasteCostTotals = Pick<WasteCostGroup, 'metres' | 'waste_kg' | 'waste_eur' | 'price_partial'> & {
  // IND-035: el € por 100 m solo cuenta los meses con €.
  eur_total: number | null;
  eur_metres: number;
  eur_months: number;
};

/** Nivel 1: el grupo en un mes o, sin mes, el total del año. */
export function wasteCostTotals(view: WasteCostView, group: string, month: number | null): WasteCostTotals {
  const rows = view.months
    .filter((item) => month === null || item.month === month)
    .flatMap((item) => item.groups.filter((row) => row.group === group));
  const withEur = rows.filter((row) => row.waste_eur !== null && row.metres > 0);
  return {
    eur_total: sumKnown(withEur.map((row) => row.waste_eur)),
    eur_metres: withEur.reduce((total, row) => total + row.metres, 0),
    eur_months: withEur.length,
    metres: rows.reduce((total, row) => total + row.metres, 0),
    waste_kg: sumKnown(rows.map((row) => row.waste_kg)),
    waste_eur: sumKnown(rows.map((row) => row.waste_eur)),
    price_partial: rows.some((row) => row.price_partial),
  };
}

export type WasteCostProductRow = {
  product_id: string;
  code: string | null;
  name: string | null;
  metres: number;
  weight_per_metre: number | null;
  waste_kg: number | null;
  waste_eur: number | null;
  kg_per_100m: number | null;
  eur_per_100m: number | null;
  share_pct: number | null;
};

/** Nivel 2: reparto del grupo en un mes o, sin mes, suma de los meses. */
export function wasteCostProducts(view: WasteCostView, group: string, month: number | null): WasteCostProductRow[] {
  const merged = new Map<string, { metres: number; eurMetres: number; weight: number; kg: Array<number | null>; eur: Array<number | null> }>();
  for (const item of view.months) {
    if (month !== null && item.month !== month) continue;
    for (const product of item.groups.find((row) => row.group === group)?.products ?? []) {
      const entry = merged.get(product.product_id) ?? { metres: 0, eurMetres: 0, weight: 0, kg: [], eur: [] };
      entry.metres += product.metres;
      if (product.waste_eur !== null) entry.eurMetres += product.metres;
      entry.weight += product.weight_kg;
      entry.kg.push(product.waste_kg);
      entry.eur.push(product.waste_eur);
      merged.set(product.product_id, entry);
    }
  }
  const rows = [...merged].map(([productId, entry]) => ({
    product_id: productId,
    metres: entry.metres,
    eurMetres: entry.eurMetres,
    weight: entry.weight,
    waste_kg: sumKnown(entry.kg),
    waste_eur: sumKnown(entry.eur),
  }));
  const totalEur = sumKnown(rows.map((row) => row.waste_eur));
  const totalKg = sumKnown(rows.map((row) => row.waste_kg));
  return rows.map((row) => {
    const label = view.products[row.product_id];
    const basis = totalEur ? [row.waste_eur, totalEur] : [row.waste_kg, totalKg];
    return {
      product_id: row.product_id,
      code: label?.code ?? null,
      name: label?.name ?? null,
      metres: row.metres,
      weight_per_metre: row.metres > 0 ? row.weight / row.metres : null,
      waste_kg: row.waste_kg,
      waste_eur: row.waste_eur,
      kg_per_100m: per100(row.waste_kg, row.metres),
      eur_per_100m: per100(row.waste_eur, row.eurMetres),
      share_pct: basis[0] !== null && basis[1] ? (basis[0] / basis[1]) * 100 : null,
    };
  });
}

export const WASTE_COST_REASONS: Record<WasteCostExclusionReason, string> = {
  no_rules: 'Sin escandallo',
  no_weight: 'Escandallo sin peso',
  no_destination: 'Usa materias primas sin destino',
};

export type WasteOutProduct = {
  product_id: string;
  code: string;
  name: string | null;
  metres: number;
  reason: string;
};

export type WasteOutMaterial = {
  key: string;
  code: string;
  problem: string;
  months: number[];
  product_count: number;
};

export type WasteOutOfShare = {
  products: WasteOutProduct[];
  materials: WasteOutMaterial[];
  metres: number;
  pct: number | null;
};

/** IND-036: lo que queda fuera del reparto en un mes o, sin mes, en el año. */
export function wasteOutOfShare(view: WasteCostView, month: number | null): WasteOutOfShare {
  const months = view.months.filter((item) => month === null || item.month === month);
  const products = new Map<string, { metres: number; byReason: Map<WasteCostExclusionReason, number> }>();
  const materials = new Map<string, { code: string; kind: WasteCostMaterialGap; months: number[]; users: Set<string> }>();
  for (const item of months) {
    for (const row of item.excluded) {
      const entry = products.get(row.product_id) ?? { metres: 0, byReason: new Map() };
      entry.metres += row.metres;
      entry.byReason.set(row.reason, (entry.byReason.get(row.reason) ?? 0) + row.metres);
      products.set(row.product_id, entry);
    }
    for (const row of item.missing_materials) {
      const key = `${row.kind}|${row.material_code}`;
      const entry = materials.get(key) ?? { code: row.material_code, kind: row.kind, months: [], users: new Set<string>() };
      entry.months.push(item.month);
      row.product_ids.forEach((productId) => entry.users.add(productId));
      materials.set(key, entry);
    }
  }
  const productRows = [...products].map(([productId, entry]) => {
    const label = view.products[productId];
    const code = label?.code ?? productId;
    // Si cambia de un mes a otro, el motivo con más metros.
    const [reason] = [...entry.byReason].sort((a, b) => b[1] - a[1])[0];
    return {
      product_id: productId,
      code,
      name: label?.name && label.name !== code ? label.name : null,
      metres: entry.metres,
      reason: WASTE_COST_REASONS[reason],
    };
  });
  const materialRows = [...materials].map(([key, entry]) => ({
    key,
    code: entry.code,
    problem:
      entry.kind === 'catalog'
        ? 'No está en el catálogo de materiales'
        : entry.kind === 'inactive'
          ? 'Inactivo en el catálogo'
          : entry.months.length === 1
            ? `Sin DESTINO en la hoja STP-MP-${MONTH_NAMES[entry.months[0] - 1].toUpperCase()}`
            : 'Sin DESTINO en las hojas STP-MP de esos meses',
    months: entry.months,
    product_count: entry.users.size,
  }));
  const metres = productRows.reduce((total, row) => total + row.metres, 0);
  const totalMetres = months.reduce((total, item) => total + item.total_metres, 0);
  return {
    products: productRows,
    materials: materialRows,
    metres,
    pct: totalMetres > 0 ? (metres / totalMetres) * 100 : null,
  };
}

function csvCell(value: string | number | null): string {
  // Un valor que empiece por = + - @ no se ejecuta como fórmula en Excel.
  const text = String(value ?? '');
  const safe = /^\s*[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Con «;» y BOM para que Excel lo abra bien. */
export function wasteOutCsv(out: WasteOutOfShare): string {
  const line = (cells: Array<string | number | null>) => cells.map(csvCell).join(';');
  const number = (value: number) => String(Math.round(value)).replace('.', ',');
  return `\uFEFF${[
    line(['Productos terminados fuera del reparto']),
    line(['Código', 'Nombre', 'Metros', 'Motivo']),
    ...out.products.map((row) => line([row.code, row.name, number(row.metres), row.reason])),
    '',
    line(['Materias primas sin destino']),
    line(['Código', 'Qué pasa', 'Meses', 'Productos que la usan']),
    ...out.materials.map((row) =>
      line([row.code, row.problem, row.months.map((month) => MONTH_SHORT[month - 1]).join(', '), row.product_count]),
    ),
  ].join('\r\n')}\r\n`;
}

/** Último año con meses que mirar: en enero, el año pasado. */
export function defaultWasteYear(today: Date = new Date()): number {
  return today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
}

/** «sep. 1,52 %» y «falta octubre» del botón del Panel. */
export function wasteButtonSummary(view: WasteControlView | undefined): {
  month: string | null;
  pct: number | null;
  missing: string | null;
} {
  const closed = view?.closed_month ?? null;
  const item = view?.months.find((entry) => entry.month === closed);
  return {
    month: closed ? MONTH_SHORT[closed - 1] : null,
    pct: groupPct(item, 'DESPERDICIO'),
    missing: view?.pending ? MONTH_NAMES[view.pending.month - 1].toLowerCase() : null,
  };
}

/** Qué falta del mes pendiente. */
export function pendingText(view: WasteControlView): string | null {
  const pending = view.pending;
  if (!pending) return null;
  const name = MONTH_NAMES[pending.month - 1].toLowerCase();
  const parts = [
    pending.missing_kg ? 'registrar los kg de desperdicio' : null,
    pending.missing_book ? `cargar el libro con el recuento de cierre (hoja de ${name})` : null,
  ].filter(Boolean);
  return `Falta ${name}: ${parts.join(' y ')}.`;
}
